import { ChevronRight, Copy, MoreHorizontal } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { IOrganization } from '@/models'
import { RoutePaths } from '@/routes/routes'

const MAX_TAGS = 3

export const getInitials = (name?: string | null) =>
  (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?'

export const formatDate = (value?: string | null) => {
  if (!value) return undefined

  const date = new Date(value)

  return Number.isNaN(date.getTime())
    ? undefined
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
}

type OrganizationHeaderProps = {
  organization?: IOrganization
  parentOrganization?: { id?: string; name?: string | null }
  pricingTierName?: string
  isSandbox: boolean
  isMarkedForCleanup: boolean
  canDelete: boolean
  onOpenSecureProxyWhitelist: () => void
  onFactoryReset: () => void
  onDelete: () => void
}

export const OrganizationHeader = ({
  organization,
  parentOrganization,
  pricingTierName,
  isSandbox,
  isMarkedForCleanup,
  canDelete,
  onOpenSecureProxyWhitelist,
  onFactoryReset,
  onDelete
}: OrganizationHeaderProps) => {
  const tags = organization?.tags ?? []
  const created = formatDate(organization?.created_date)

  const copyId = () => {
    if (!organization?.id) return

    navigator.clipboard
      .writeText(organization.id)
      .then(() =>
        toast({ title: 'Copied', description: 'Organization ID copied' })
      )
      .catch(() =>
        toast({ title: 'Failed', description: 'Copy the ID manually' })
      )
  }

  return (
    <header className="flex flex-wrap items-start justify-between gap-4 md:flex-nowrap">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span
          aria-hidden
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-bold"
        >
          {getInitials(organization?.name)}
        </span>
        <div className="min-w-0 space-y-1.5">
          <nav
            aria-label="Breadcrumb"
            className="flex flex-wrap items-center gap-1.5 text-sm"
          >
            <Link
              className="text-muted-foreground hover:text-foreground"
              to={RoutePaths.ORGANIZATIONS}
            >
              Organizations
            </Link>
            <ChevronRight
              aria-hidden
              className="h-4 w-4 text-muted-foreground"
            />
            <span
              aria-current="page"
              className="truncate text-xl font-semibold tracking-tight"
            >
              {organization?.name || 'Organization'}
            </span>
          </nav>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {isMarkedForCleanup && (
              <Badge variant="destructive">Marked for cleanup</Badge>
            )}
            {organization?.organization_use && (
              <Badge
                className={cn(
                  'border-transparent',
                  isSandbox
                    ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300'
                    : 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
                )}
                variant="outline"
              >
                {organization.organization_use}
              </Badge>
            )}
            {organization?.subscription_status && (
              <Badge
                className={cn(
                  'border-transparent',
                  organization.subscription_status === 'Churning'
                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                    : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                )}
                variant="outline"
              >
                Subscription {organization.subscription_status.toLowerCase()}
              </Badge>
            )}
            {pricingTierName && (
              <Badge variant="secondary">{pricingTierName}</Badge>
            )}
            {tags
              .filter((tag) => tag !== 'Marked for Cleanup')
              .slice(0, MAX_TAGS)
              .map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            {tags.length > MAX_TAGS && <span>+{tags.length - MAX_TAGS}</span>}
            <span aria-hidden className="h-3.5 w-px bg-border" />
            <span>
              ID{' '}
              <span className="font-mono text-foreground">
                {organization?.id}
              </span>
            </span>
            {organization?.type && <span>· {organization.type}</span>}
            {created && <span>· Created {created}</span>}
            {parentOrganization?.id && (
              <span>
                · Parent{' '}
                <Link
                  className="text-foreground underline-offset-4 hover:underline"
                  to={`${RoutePaths.ORGANIZATIONS}/${parentOrganization.id}`}
                >
                  {parentOrganization.name || parentOrganization.id}
                </Link>
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button onClick={copyId} size="sm" variant="outline">
          <Copy className="mr-2 h-4 w-4" />
          Copy ID
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button aria-label="More actions" size="sm" variant="outline">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={onOpenSecureProxyWhitelist}>
              Secure proxy whitelist
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {isSandbox && (
              <DropdownMenuItem
                className="text-destructive"
                disabled={!canDelete || isMarkedForCleanup}
                onSelect={onFactoryReset}
              >
                Factory reset sandbox
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-destructive"
              disabled={!canDelete || isMarkedForCleanup}
              onSelect={onDelete}
            >
              Delete organization
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
